import { useCallback } from 'react';
import { isFuture } from 'date-fns';
import { type ProjectType, SOFTWARE_PROJECT, CORE_PROJECT } from '@atlassian/jira-common-constants';
import {
	type SupportedProjectType,
	getStatSigCohortAndExposureFunction,
} from '@atlassian/jira-lock-screen-experiment-common';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type EntitlementDetails,
	type ApplicationEdition,
	STANDARD_EDITION,
	PREMIUM_EDITION,
} from '@atlassian/jira-shared-types';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';

export const useFeatureExposedEvent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSiteAdmin = useIsSiteAdmin();

	return useCallback(
		(
			ineligibilityReason: string | null,
			projectType: SupportedProjectType,
			attributes: Record<string, unknown> = {},
			edition: ApplicationEdition | undefined,
		) => {
			const { cohort, fireExpExperimentExposure } = getStatSigCohortAndExposureFunction({
				projectType,
				edition,
			});

			if (ineligibilityReason === null) {
				fireExpExperimentExposure();

				fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', 'trialEndLockScreen', {
					...(ineligibilityReason === null
						? { cohort }
						: {
								ineligibilityReason,
								originalCohort: cohort,
								cohort: 'not-enrolled',
							}),
					isSiteAdmin,
					growthInitiative: 'transmuters',
					...attributes,
				});
			}
		},
		[createAnalyticsEvent, isSiteAdmin],
	);
};

export const getLocalEligibility = ({
	projectType,
	edition,
	entitlementDetails,
	billingSourceSystem,
	entitlementId,
}: Partial<{
	projectType: ProjectType;
	edition: ApplicationEdition;
	entitlementDetails: EntitlementDetails;
	billingSourceSystem: 'CCP' | 'HAMS';
	entitlementId: string;
}>) => {
	if (
		projectType === SOFTWARE_PROJECT &&
		edition !== PREMIUM_EDITION &&
		edition !== STANDARD_EDITION
	) {
		return {
			eligible: false,
			ineligibilityReason: 'invalidSoftwareProjectTypeAndEditionCombo',
		};
	}

	if (projectType === CORE_PROJECT && edition !== PREMIUM_EDITION) {
		return {
			eligible: false,
			ineligibilityReason: 'invalidBusinessProjectTypeAndEditionCombo',
		};
	}

	if (
		projectType !== SOFTWARE_PROJECT &&
		projectType !== CORE_PROJECT &&
		edition !== STANDARD_EDITION
	) {
		return { eligible: false, ineligibilityReason: 'invalidEdition' };
	}

	if (!entitlementDetails) {
		return { eligible: false, ineligibilityReason: 'missingEntitlementDetails' };
	}

	if (billingSourceSystem !== 'CCP') {
		return { eligible: false, ineligibilityReason: 'notCCP' };
	}

	if (entitlementDetails?.trialEndTime && isFuture(entitlementDetails.trialEndTime)) {
		return { eligible: false, ineligibilityReason: 'inTrial' };
	}

	if (!entitlementId) {
		return { eligible: false, ineligibilityReason: 'missingEntitlementId' };
	}

	return { eligible: true, ineligibilityReason: '' };
};

// Used to support custom Error objects created by inheriting another Error object.
// Object.getOwnPropertyNames is used to fetch all properties from nested prototypes.
// We are removing the 'stack' as we are adding the transformed error into analytic events.
// The 'cause' property of an Error instance is also an Error instance indicating the
// specific original cause of the error, in which case we only want to keep the 'message'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformError = (error: any) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const errorObject = {} as any;
	Object.getOwnPropertyNames(error)
		.filter((name) => name !== 'stack')
		.forEach((name) => {
			if (name === 'cause') {
				errorObject[name] = error[name].message;
			} else {
				errorObject[name] = error[name];
			}
		});
	return errorObject;
};
